import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import recurringService from '../services/recurringService';

// --- Ingresos recurrentes ---

export const fetchIncomes = createAsyncThunk('recurring/fetchIncomes', async (_, { rejectWithValue }) => {
  try {
    const response = await recurringService.getIncomes();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const addIncome = createAsyncThunk('recurring/addIncome', async (data, { rejectWithValue }) => {
  try {
    const response = await recurringService.createIncome(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const updateIncome = createAsyncThunk('recurring/updateIncome', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await recurringService.updateIncome(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const deleteIncome = createAsyncThunk('recurring/deleteIncome', async (id, { rejectWithValue }) => {
  try {
    await recurringService.deleteIncome(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

// --- Gastos recurrentes ---

export const fetchExpenses = createAsyncThunk('recurring/fetchExpenses', async (_, { rejectWithValue }) => {
  try {
    const response = await recurringService.getExpenses();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const addExpense = createAsyncThunk('recurring/addExpense', async (data, { rejectWithValue }) => {
  try {
    const response = await recurringService.createExpense(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const updateExpense = createAsyncThunk('recurring/updateExpense', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await recurringService.updateExpense(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const deleteExpense = createAsyncThunk('recurring/deleteExpense', async (id, { rejectWithValue }) => {
  try {
    await recurringService.deleteExpense(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

// --- Excepciones (omitir / editar una sola ocurrencia) ---

export const fetchExceptions = createAsyncThunk(
  'recurring/fetchExceptions',
  async ({ itemTipo, itemId }, { rejectWithValue }) => {
    try {
      const response = await recurringService.getExceptions(itemTipo, itemId);
      return { itemTipo, itemId, exceptions: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createException = createAsyncThunk(
  'recurring/createException',
  async ({ itemTipo, itemId, data }, { rejectWithValue }) => {
    try {
      const response = await recurringService.createException(itemTipo, itemId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteException = createAsyncThunk(
  'recurring/deleteException',
  async ({ exceptionId, itemTipo, itemId }, { rejectWithValue }) => {
    try {
      await recurringService.deleteException(exceptionId);
      return { exceptionId, itemTipo, itemId };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  incomes: [],
  expenses: [],
  exceptionsByItem: {}, // key: `${item_tipo}-${item_id}` -> array de excepciones
  loading: false,
  error: null,
};

const exceptionKey = (tipo, id) => `${tipo}-${id}`;

const recurringSlice = createSlice({
  name: 'recurring',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Ingresos
      .addCase(fetchIncomes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncomes.fulfilled, (state, action) => {
        state.loading = false;
        state.incomes = action.payload;
      })
      .addCase(fetchIncomes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addIncome.fulfilled, (state, action) => {
        state.incomes.push(action.payload);
      })
      .addCase(updateIncome.fulfilled, (state, action) => {
        const idx = state.incomes.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.incomes[idx] = action.payload;
      })
      .addCase(deleteIncome.fulfilled, (state, action) => {
        state.incomes = state.incomes.filter((i) => i.id !== action.payload);
      })

      // Gastos
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = action.payload;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.expenses.push(action.payload);
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        const idx = state.expenses.findIndex((e) => e.id === action.payload.id);
        if (idx !== -1) state.expenses[idx] = action.payload;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.expenses = state.expenses.filter((e) => e.id !== action.payload);
      })

      // Excepciones
      .addCase(fetchExceptions.fulfilled, (state, action) => {
        const key = exceptionKey(action.payload.itemTipo, action.payload.itemId);
        state.exceptionsByItem[key] = action.payload.exceptions;
      })
      .addCase(createException.fulfilled, (state, action) => {
        const key = exceptionKey(action.payload.item_tipo, action.payload.item_id);
        const list = state.exceptionsByItem[key] || [];
        const idx = list.findIndex((e) => e.fecha_ocurrencia === action.payload.fecha_ocurrencia);
        if (idx !== -1) {
          list[idx] = action.payload;
        } else {
          list.push(action.payload);
        }
        state.exceptionsByItem[key] = list;
      })
      .addCase(deleteException.fulfilled, (state, action) => {
        const key = exceptionKey(action.payload.itemTipo, action.payload.itemId);
        state.exceptionsByItem[key] = (state.exceptionsByItem[key] || []).filter(
          (e) => e.id !== action.payload.exceptionId
        );
      });
  },
});

export const { clearError } = recurringSlice.actions;
export default recurringSlice.reducer;
